import React from 'react';
import {
  Box,
  Button,
  Checkbox,
  Divider,
  FormControlLabel,
  IconButton,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import {
  CartLine,
  PriceBasis,
  computeTotals,
  formatCents,
  isCustom,
  isMissingCost,
  lineKey,
  lineTotal,
  retailTotals,
  unitPrice,
} from './money';
import { COLORS } from './theme';

type Props = {
  lines: CartLine[];
  basis: PriceBasis;
  onBasisChange: (basis: PriceBasis) => void;
  /** Whether to offer the owner-withdrawal control at all. God role
   *  only; the server refuses the basis regardless, so this is about
   *  not putting a dangerous switch in front of a cashier. */
  canWithdraw: boolean;
  /** Keyed by `lineKey(hit)`, not the bare item id — ids collide
   *  across tenants. */
  onQtyChange: (key: string, delta: number) => void;
  onRemove: (key: string) => void;
  onClear: () => void;
  onPurchase: () => void;
};

const CartPanel: React.FC<Props> = ({
  lines,
  basis,
  onBasisChange,
  canWithdraw,
  onQtyChange,
  onRemove,
  onClear,
  onPurchase,
}) => {
  const employee = basis === 'employee';
  const owner = basis === 'owner';
  const totals = computeTotals(lines, basis);
  const retail = retailTotals(lines);
  const savings = retail.total - totals.total;
  const empty = lines.length === 0;

  return (
    <Box
      sx={{
        width: 420,
        flexShrink: 0,
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: employee ? COLORS.warnBg : COLORS.surface,
        // 3px amber frame in employee mode: the whole rail changes so
        // it's impossible to be in cost pricing and not notice.
        borderLeft: employee
          ? `3px solid ${COLORS.warn}`
          : `1px solid ${COLORS.border}`,
        transition: 'background-color 120ms ease',
      }}
    >
      <Box
        sx={{
          px: 2,
          py: 1.5,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Typography sx={{ fontWeight: 700, letterSpacing: '0.08em' }}>
          CURRENT SALE
        </Typography>
        <Button
          size="small"
          color="inherit"
          onClick={onClear}
          disabled={empty}
          sx={{ color: COLORS.textMuted, minHeight: 36 }}
        >
          Clear
        </Button>
      </Box>

      {employee && (
        <Box
          sx={{
            mx: 2,
            mb: 1,
            px: 1.5,
            py: 1,
            borderRadius: 1.5,
            backgroundColor: COLORS.warn,
            color: '#ffffff',
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}
        >
          <WarningAmberIcon fontSize="small" />
          <Typography sx={{ fontWeight: 700, fontSize: '0.9rem' }}>
            EMPLOYEE PRICING — COST BASIS
          </Typography>
        </Box>
      )}

      <Box sx={{ flex: 1, overflowY: 'auto', px: 2 }}>
        {empty ? (
          <Box
            sx={{
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: COLORS.textMuted,
              textAlign: 'center',
            }}
          >
            <Typography variant="body1">Scan an item to start</Typography>
          </Box>
        ) : (
          lines.map((line) => (
            <CartLineRow
              key={lineKey(line.hit)}
              line={line}
              basis={basis}
              onQtyChange={onQtyChange}
              onRemove={onRemove}
            />
          ))
        )}
      </Box>

      <Box sx={{ px: 2, pb: 2, pt: 1 }}>
        <Divider sx={{ mb: 1.5 }} />
        <TotalRow label="Subtotal" value={totals.subtotal} />
        <TotalRow label="HST (14%)" value={totals.tax} />
        <Divider sx={{ my: 1 }} />
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'baseline',
            mb: employee ? 0.5 : 1.5,
          }}
        >
          <Typography sx={{ fontWeight: 700, letterSpacing: '0.08em' }}>
            TOTAL
          </Typography>
          <Typography sx={{ fontWeight: 700, fontSize: '2rem', lineHeight: 1.1 }}>
            {formatCents(totals.total)}
          </Typography>
        </Box>
        {employee && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              mb: 1.5,
              color: COLORS.warnText,
            }}
          >
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              Saved vs retail
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 700 }}>
              {formatCents(savings)}
            </Typography>
          </Box>
        )}

        <FormControlLabel
          control={
            <Checkbox
              checked={employee}
              onChange={(e) => onBasisChange(e.target.checked ? 'employee' : 'retail')}
              sx={{
                color: COLORS.warn,
                '&.Mui-checked': { color: COLORS.warn },
                // Bigger than MUI's default: this is a checkbox people
                // tap in a hurry, and a miss is a pricing error.
                '& .MuiSvgIcon-root': { fontSize: 30 },
              }}
            />
          }
          label={
            <Typography sx={{ fontWeight: 600 }}>Employee discount</Typography>
          }
          sx={{ mb: 1.5, ml: -0.5 }}
        />

        {canWithdraw && (
          <FormControlLabel
            control={
              <Checkbox
                checked={owner}
                onChange={(e) => onBasisChange(e.target.checked ? 'owner' : 'retail')}
                sx={{
                  color: COLORS.danger,
                  '&.Mui-checked': { color: COLORS.danger },
                  '& .MuiSvgIcon-root': { fontSize: 30 },
                }}
              />
            }
            label={
              <Box>
                <Typography sx={{ fontWeight: 600 }}>Owner withdrawal</Typography>
                <Typography variant="caption" sx={{ color: COLORS.textMuted }}>
                  At cost, no tax, no payment. Stock still comes off the shelf.
                </Typography>
              </Box>
            }
            sx={{ mb: 1.5, ml: -0.5 }}
          />
        )}

        <Button
          fullWidth
          variant="contained"
          disabled={empty}
          onClick={onPurchase}
          startIcon={employee || owner ? <WarningAmberIcon /> : undefined}
          sx={{
            py: 2.5,
            fontSize: '1.35rem',
            fontWeight: 700,
            backgroundColor: owner
              ? COLORS.danger
              : employee
                ? COLORS.warn
                : COLORS.accent,
            '&:hover': {
              backgroundColor: owner
                ? '#8e1616'
                : employee
                  ? '#d97706'
                  : COLORS.accentDark,
            },
          }}
        >
          {owner
            ? 'WITHDRAW — OWNER '
            : employee
              ? 'PURCHASE — EMPLOYEE '
              : 'PURCHASE  ·  '}
          {formatCents(totals.total)}
        </Button>
      </Box>
    </Box>
  );
};

const CartLineRow: React.FC<{
  line: CartLine;
  basis: PriceBasis;
  onQtyChange: (key: string, delta: number) => void;
  onRemove: (key: string) => void;
}> = ({ line, basis, onQtyChange, onRemove }) => {
  // Cost-priced either way; the row only needs to know it isn't retail.
  const employee = basis === 'employee' || basis === 'owner';
  const unit = unitPrice(line, basis);
  const missingCost = isMissingCost(line, basis);
  const retailUnit = line.hit.price ?? 0;
  const key = lineKey(line.hit);

  return (
    <Box
      sx={{
        backgroundColor: COLORS.surface,
        border: `1px solid ${COLORS.border}`,
        borderRadius: 2,
        p: 1.5,
        mb: 1,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontWeight: 600, lineHeight: 1.3 }}>
            {line.hit.name}
          </Typography>
          {/* Which shop this came from. The till spans three catalogs,
              so the same-sounding product can exist in more than one. */}
          <Typography
            variant="caption"
            sx={{ color: COLORS.textMuted, fontWeight: 600 }}
          >
            {line.hit.tenant_label}
          </Typography>
        </Box>
        <IconButton
          size="small"
          onClick={() => onRemove(key)}
          aria-label={`Remove ${line.hit.name}`}
          sx={{ color: COLORS.textMuted, mt: -0.5, mr: -0.5 }}
        >
          <DeleteOutlineIcon fontSize="small" />
        </IconButton>
      </Box>

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mt: 0.5,
        }}
      >
        <Box>
          <Typography variant="body2" sx={{ color: COLORS.textMuted }}>
            {employee ? 'cost ' : ''}
            {formatCents(unit)} ea
          </Typography>
          {/* Struck-through retail so the give-up is visible per line,
              not just in the total. */}
          {employee && !missingCost && (
            <Typography
              variant="body2"
              sx={{ color: COLORS.textMuted, textDecoration: 'line-through' }}
            >
              was {formatCents(retailUnit)}
            </Typography>
          )}
          {missingCost && (
            <Typography
              variant="body2"
              sx={{ color: COLORS.warnText, fontWeight: 700 }}
            >
              {isCustom(line.hit)
                ? 'custom price — not discounted'
                : 'no cost on file — at retail'}
            </Typography>
          )}
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <IconButton
            onClick={() => onQtyChange(key, -1)}
            aria-label={`Decrease quantity of ${line.hit.name}`}
            sx={{ border: `1px solid ${COLORS.border}` }}
          >
            <RemoveIcon fontSize="small" />
          </IconButton>
          <Typography
            sx={{ minWidth: 32, textAlign: 'center', fontWeight: 700, fontSize: '1.15rem' }}
          >
            {line.qty}
          </Typography>
          <IconButton
            onClick={() => onQtyChange(key, 1)}
            aria-label={`Increase quantity of ${line.hit.name}`}
            sx={{ border: `1px solid ${COLORS.border}` }}
          >
            <AddIcon fontSize="small" />
          </IconButton>
        </Box>
      </Box>

      <Typography sx={{ textAlign: 'right', fontWeight: 700, fontSize: '1.1rem', mt: 0.5 }}>
        {formatCents(lineTotal(line, basis))}
      </Typography>
    </Box>
  );
};

const TotalRow: React.FC<{ label: string; value: number }> = ({ label, value }) => (
  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
    <Typography sx={{ color: COLORS.textMuted }}>{label}</Typography>
    <Typography sx={{ fontWeight: 600, fontSize: '1.1rem' }}>
      {formatCents(value)}
    </Typography>
  </Box>
);

export default CartPanel;
